import { useState } from "react";
import { ArrowUpRight, PenTool, Monitor, Share2, Users, Zap, Megaphone, Check } from "lucide-react";
import { SERVICES, CLIENTS } from "@/lib/data";
import { IOSHighlight } from "@/components/site/iOSKit";

const ICONS = [PenTool, Monitor, Share2, Users, Zap, Megaphone];

// Paleta rotando rojo / blanco / gris / negro
const THEMES = [
    { bg: "#FF2A2A", text: "#ffffff", accent: "#0A0A0A", sub: "rgba(255,255,255,0.85)", chip: "rgba(0,0,0,0.18)" },
    { bg: "#F3F4F6", text: "#0A0A0A", accent: "#FF2A2A", sub: "rgba(10,10,10,0.7)", chip: "rgba(0,0,0,0.08)" },
    { bg: "#1a1a1a", text: "#ffffff", accent: "#FF2A2A", sub: "rgba(255,255,255,0.65)", chip: "rgba(255,255,255,0.08)" },
    { bg: "#0A0A0A", text: "#ffffff", accent: "#FF2A2A", sub: "rgba(255,255,255,0.6)", chip: "rgba(255,255,255,0.06)" },
    { bg: "#FF2A2A", text: "#ffffff", accent: "#0A0A0A", sub: "rgba(255,255,255,0.85)", chip: "rgba(0,0,0,0.18)" },
    { bg: "#F3F4F6", text: "#0A0A0A", accent: "#FF2A2A", sub: "rgba(10,10,10,0.7)", chip: "rgba(0,0,0,0.08)" },
];

const ServiceColumn = ({ service, theme, index, isOpen, anyOpen, onHover, onLeave }) => {
    const Icon = ICONS[index % ICONS.length];

    // Flex basis animation — hovered expands, others shrink (only on lg+)
    const flexStyle = {
        flex: isOpen ? "4 1 0%" : anyOpen ? "1 1 0%" : "1.5 1 0%",
    };

    return (
        <a
            href="#contacto"
            data-testid={`service-col-${service.n}`}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
            className="group relative flex flex-col justify-between overflow-hidden h-[460px] md:h-[560px] lg:h-[640px] transition-[flex] duration-700 cursor-pointer"
            style={{
                backgroundColor: theme.bg,
                color: theme.text,
                ...flexStyle,
            }}
        >
            {/* Top chrome: number + icon */}
            <div className="flex items-start justify-between p-5 md:p-6 relative z-10">
                <span
                    className="font-mono text-[11px] tracking-[0.2em]"
                    style={{ color: theme.sub }}
                >
                    ({service.n})
                </span>
                <div
                    className="w-10 h-10 rounded-[11px] flex items-center justify-center transition-all duration-500 group-hover:rotate-[-6deg] group-hover:scale-110"
                    style={{
                        backgroundColor: theme.accent,
                        color: theme.bg,
                    }}
                >
                    <Icon className="w-4 h-4" />
                </div>
            </div>

            {/* CLOSED STATE — vertical rotated name */}
            <div
                className={`flex-1 flex items-center justify-center transition-all duration-500 ${
                    isOpen ? "opacity-0 scale-90" : "opacity-100"
                }`}
            >
                <h3
                    className="font-display leading-[0.9] tracking-tight text-[14vw] md:text-[6vw] lg:text-[4.5vw] whitespace-nowrap"
                    style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                    }}
                >
                    {service.short}
                </h3>
            </div>

            {/* OPEN STATE — rich detail */}
            <div
                className={`absolute inset-0 flex flex-col justify-end p-6 md:p-8 transition-all duration-500 pointer-events-none ${
                    isOpen
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-6"
                }`}
            >
                <div className="mt-16 md:mt-20">
                    <div
                        className="text-[10px] tracking-[0.3em] uppercase font-bold mb-3"
                        style={{ color: theme.accent }}
                    >
                        Servicio · {service.n}
                    </div>
                    <h3 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-5 max-w-md">
                        {service.name}
                    </h3>
                    <p
                        className="text-[14px] md:text-[15px] leading-relaxed mb-6 max-w-md"
                        style={{ color: theme.sub }}
                    >
                        {service.desc}
                    </p>

                    {/* Bullets */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-16 md:mb-20 max-w-md">
                        {service.bullets.map((b) => (
                            <div
                                key={b}
                                className="flex items-center gap-2 text-[12px] md:text-[13px]"
                            >
                                <Check
                                    className="w-3.5 h-3.5 shrink-0"
                                    style={{ color: theme.accent }}
                                />
                                <span style={{ color: theme.sub }}>{b}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bottom chrome: always visible label + arrow */}
            <div
                className={`p-5 md:p-6 flex items-center justify-between relative z-10 transition-opacity duration-500 ${
                    isOpen ? "opacity-0" : "opacity-100"
                }`}
            >
                <span
                    className="text-[10px] tracking-[0.25em] uppercase font-bold"
                    style={{ color: theme.accent }}
                >
                    Click para cotizar
                </span>
                <ArrowUpRight
                    className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                    style={{ color: theme.accent }}
                />
            </div>

            {/* Persistent open-state CTA (bottom-right corner) */}
            <div
                className={`absolute bottom-6 right-6 transition-all duration-500 ${
                    isOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                }`}
            >
                <div
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] tracking-[0.2em] uppercase font-bold"
                    style={{
                        backgroundColor: theme.accent,
                        color: theme.bg,
                    }}
                >
                    Cotizar
                    <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
            </div>
        </a>
    );
};

export const Services = () => {
    const [openIndex, setOpenIndex] = useState(null);

    return (
        <section
            id="servicios"
            data-testid="services-section"
            className="relative bg-[#0A0A0A] border-t border-white/5"
        >
            {/* Clients marquee */}
            <div className="overflow-hidden border-b border-white/10 py-6">
                <div className="marquee-track">
                    {[...CLIENTS, ...CLIENTS, ...CLIENTS].map((c, i) => (
                        <span
                            key={i}
                            className="font-display text-3xl md:text-4xl px-8 flex items-center gap-8 text-white/40 whitespace-nowrap"
                        >
                            {c}
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF2A2A]/70" />
                        </span>
                    ))}
                </div>
            </div>

            <div className="px-6 md:px-12 lg:px-20 pt-24 md:pt-32 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-16">
                    <div className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-10 h-px bg-[#FF2A2A]" />
                            <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">
                                Servicios
                            </span>
                        </div>
                        <h2
                            data-testid="services-heading"
                            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-balance"
                        >
                            Cada <span className="font-serif text-white/90">pieza,</span>
                            <br />
                            con{" "}
                            <IOSHighlight variant="red">intención.</IOSHighlight>
                        </h2>
                    </div>

                    <div className="md:col-span-5 md:col-start-8 flex items-end">
                        <p className="text-white/70 text-base md:text-lg leading-relaxed font-light">
                            Del primer brief al entregable final. Trabajamos
                            end-to-end. Pasa el cursor sobre cualquier
                            servicio para más información.
                        </p>
                    </div>
                </div>
            </div>

            {/* Color columns — flex layout for expand-on-hover effect */}
            <div
                data-testid="services-grid"
                className="flex flex-col md:flex-row w-full"
                onMouseLeave={() => setOpenIndex(null)}
            >
                {SERVICES.map((s, i) => (
                    <ServiceColumn
                        key={s.n}
                        service={s}
                        theme={THEMES[i]}
                        index={i}
                        isOpen={openIndex === i}
                        anyOpen={openIndex !== null && openIndex !== i}
                        onHover={() => setOpenIndex(i)}
                        onLeave={() => {}}
                    />
                ))}
            </div>

            <div className="px-6 md:px-12 lg:px-20 py-8 flex items-center justify-between text-xs tracking-[0.25em] uppercase text-white/40 gap-4 flex-wrap">
                <span>Conectamos tu marca con el mundo digital y el mundo real.</span>
                <a
                    href="#contacto"
                    data-testid="services-cta-link"
                    className="hover:text-[#FF2A2A] transition-colors"
                >
                    Pedir cotización →
                </a>
            </div>
        </section>
    );
};
